/*
  A read-only drawing of the slab label (D5), after the portal's LabelFrontSVG and
  LabelBackSVG, drawn ONLY from the public fields D3 allows.

  The geometry is the portal's, from lib/labelLayout and lib/labelArt: the frame and its
  gaps, the PCG corner mark, the arc artwork, the grade box and every line of text sit
  where the engraved label puts them. The finish is labelFinish (Robert, 2026-10-26):
  Flawless alone is Robert's PDF, a brushed gold face with the frame, mark, lettering and
  arcs in silver and the grade in gold on a silver panel. Every other label is black, as the
  portal prints it: with its parts (frame, mark, lettering, arcs, QR and the grade panel) in
  gold above 9 and in the tile's silver at 9 and below and for Authentic, the grade knocked
  out of the panel in black. lib/labelLayout FINISHES holds the three palettes. The back
  takes the front's finish, so the pair reads as one slab.

  It is a likeness, not the print file: the portal's text is outlined from font files the
  public site does not ship, so this is SVG text in the visitor's Arial (lib/labelLayout
  says how a long line is fitted), and the portal can pick a design per office and carry
  an Error line or a signature that the public page does not have. Nothing a customer
  could mistake for a printable label is offered — no download, no design picker.

  The QR code encodes the same URL the printed label's does (POP_REPORT_URL). It is drawn
  for likeness, not offered for scanning: at this size on a screen a phone may not read it,
  and the customer is already on the page it points to. It is drawn in the label's ink
  straight onto the face, with no white square (Robert, 2026-10-21: "make it in gold to
  match label color and remove the white").
*/

import { useMemo } from 'react';
import * as QRCode from 'qrcode';

import {
  formatGrade,
  gradeFigure,
  isAuthentic,
  labelFinish,
  reportUrlFor,
  selectLabelLines,
  selectSubGrades,
  ReportRow,
} from 'lib/gradeReport';
import { CORNER_LOGO_C, CORNER_LOGO_P } from 'lib/labelArt';
import {
  BACK_LOGO_TRANSFORM,
  FINISHES,
  FinishPalette,
  FONT_FAMILY,
  FONT_WEIGHT,
  FRONT_LOGO_TRANSFORM,
  GRADE_BOX,
  LH,
  LW,
  QR_SIZE,
  QR_X,
  QR_Y,
  TextSpec,
  backFramePath,
  backLayout,
  frontLayout,
  subGradeLayout,
} from 'lib/labelLayout';
import { useSvgId } from 'lib/useSvgId';


/* One path of unit squares for the dark modules: one element, not hundreds. */
export const qrPath = (text: string, size: number): string => {
  const qr = QRCode.create(text, { errorCorrectionLevel: 'L' });
  const n = qr.modules.size;
  const s = size / n;
  let d = '';
  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      if (qr.modules.data[y * n + x]) {
        d += `M${(x * s).toFixed(2)} ${(y * s).toFixed(2)}h${s.toFixed(2)}v${s.toFixed(2)}h-${s.toFixed(2)}z`;
      }
    }
  }
  return d;
};

/*
  The PDF's thin outline under the ink, for a path or a line of text. Painted under the
  fill (paint-order stroke), so it never thins the shape it outlines.
*/
const outline = (stroke: string | null | undefined, width: number) =>
  stroke ? { stroke, strokeWidth: width, strokeLinejoin: 'round' as const, paintOrder: 'stroke' } : {};

/* One laid-out line. textLength is set only on a line lib/labelLayout fitted to its room. */
function LabelText({ spec, fill, stroke }: { spec: TextSpec; fill: string; stroke?: string | null }) {
  return (
    <text
      x={spec.x}
      y={spec.y}
      fontSize={spec.fontSize}
      fontFamily={FONT_FAMILY[spec.weight]}
      fontWeight={FONT_WEIGHT[spec.weight]}
      letterSpacing={spec.letterSpacing || undefined}
      textAnchor={spec.anchor}
      textLength={spec.textLength}
      lengthAdjust={spec.textLength ? 'spacingAndGlyphs' : undefined}
      fill={fill}
      {...outline(stroke, Math.max(0.35, spec.fontSize * 0.035))}
    >
      {spec.text}
    </text>
  );
}

function CornerMark({ transform, fill, stroke }: { transform: string; fill: string; stroke?: string | null }) {
  /* In the mark's own units, which the transform scales up 1.8 times. */
  return (
    <>
      <path d={CORNER_LOGO_P} transform={transform} fill={fill} {...outline(stroke, 0.25)} />
      <path d={CORNER_LOGO_C} transform={transform} fill={fill} {...outline(stroke, 0.25)} />
    </>
  );
}

/*
  The face: the finish's gradient, brushed where the finish asks for it. Fractal noise stretched along x (a low x
  frequency, a high y one) reads as fine horizontal streaks. It is greyed, squeezed toward
  mid-grey by the finish's brush strength and soft-lit over the gradient, so mid-grey leaves
  the metal as it is and the streaks only lighten or darken it; the result is cut back to
  the face's own rounded shape. A hairline edge keeps a metal label's outline on any page.
*/
function Face({ id, finish }: { id: string; finish: FinishPalette }) {
  const [top, middle, bottom] = finish.face;
  const slope = finish.brush;
  const intercept = (1 - slope) / 2;
  return (
    <>
      <defs>
        <linearGradient id={`${id}-face`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={top} />
          <stop offset="0.55" stopColor={middle} />
          <stop offset="1" stopColor={bottom} />
        </linearGradient>
        {slope > 0 && (
        <filter id={`${id}-brush`} x="0" y="0" width="1" height="1" colorInterpolationFilters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency="0.011 0.9" numOctaves={2} seed={7} result="noise" />
          <feColorMatrix in="noise" type="saturate" values="0" result="grey" />
          <feComponentTransfer in="grey" result="streaks">
            <feFuncR type="linear" slope={slope} intercept={intercept} />
            <feFuncG type="linear" slope={slope} intercept={intercept} />
            <feFuncB type="linear" slope={slope} intercept={intercept} />
          </feComponentTransfer>
          <feBlend in="streaks" in2="SourceGraphic" mode="soft-light" result="brushed" />
          <feComposite in="brushed" in2="SourceGraphic" operator="in" />
        </filter>
        )}
      </defs>
      <rect
        data-label-face=""
        x={0.25}
        y={0.25}
        width={LW - 0.5}
        height={LH - 0.5}
        rx={4}
        fill={`url(#${id}-face)`}
        filter={slope > 0 ? `url(#${id}-brush)` : undefined}
      />
      {finish.edge && (
        <rect x={0.25} y={0.25} width={LW - 0.5} height={LH - 0.5} rx={4} fill="none" stroke={finish.edge} strokeWidth={0.5} />
      )}
    </>
  );
}

function LabelFront({ row, cardName, detail }: { row: ReportRow; cardName: string; detail: string }) {
  const id = useSvgId('pcg-label-front');
  const authentic = isAuthentic(row);
  const finishName = labelFinish(row);
  const finish = FINISHES[finishName];
  const { ink, inkStroke } = finish;
  const layout = frontLayout({
    cardName,
    detail,
    gradeName: authentic ? 'Authentic' : row.grade || '',
    figure: gradeFigure(row),
    authentic,
  });
  /* An Authentication label has no sub-grades, as on the engraved one. */
  const subs = authentic
    ? []
    : subGradeLayout(selectSubGrades(row).map((sub) => ({ ...sub, text: formatGrade(sub.value) })));

  return (
    <svg viewBox={`0 0 ${LW} ${LH}`} className="block h-auto w-full" aria-hidden="true" data-label-finish={finishName}>
      <Face id={id} finish={finish} />

      {/* The grade panel, full height on the right: silver on the gold and black-silver labels, gold on the black-gold. */}
      <defs>
        <linearGradient id={`${id}-panel`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={finish.panel[0]} />
          <stop offset="0.55" stopColor={finish.panel[0]} />
          <stop offset="1" stopColor={finish.panel[1]} />
        </linearGradient>
      </defs>
      <rect x={GRADE_BOX.x} y={GRADE_BOX.y} width={GRADE_BOX.w} height={GRADE_BOX.h} fill={`url(#${id}-panel)`} />
      {layout.gradeName && <LabelText spec={layout.gradeName} fill={finish.gradeInk} stroke={finish.gradeStroke} />}
      {layout.figure && <LabelText spec={layout.figure} fill={finish.gradeInk} stroke={finish.gradeStroke} />}

      <path d={layout.frame} fill={ink} {...outline(inkStroke, 0.3)} />
      <CornerMark transform={FRONT_LOGO_TRANSFORM} fill={ink} stroke={inkStroke} />
      {layout.certifiedText && <LabelText spec={layout.certifiedText} fill={ink} stroke={inkStroke} />}

      {layout.name && <LabelText spec={layout.name} fill={ink} stroke={inkStroke} />}
      {layout.detail && <LabelText spec={layout.detail} fill={ink} stroke={inkStroke} />}

      {subs.map((sub) => (
        <g key={sub.title.text}>
          {sub.score && <LabelText spec={sub.score} fill={ink} stroke={inkStroke} />}
          <LabelText spec={sub.title} fill={ink} stroke={inkStroke} />
          {/* The outline is in the artwork's own units, which the transform scales down. */}
          {sub.arc && <path d={sub.arc} transform={sub.transform} fill={ink} {...outline(inkStroke, 3)} />}
        </g>
      ))}
    </svg>
  );
}

function LabelBack({ row, cardName, lines }: { row: ReportRow; cardName: string; lines: string[] }) {
  const id = useSvgId('pcg-label-back');
  const finishName = labelFinish(row);
  const finish = FINISHES[finishName];
  const { ink, inkStroke } = finish;
  const d = useMemo(() => qrPath(reportUrlFor(row.gradedId), QR_SIZE), [row.gradedId]);
  const layout = backLayout({ cardName, lines, gradedId: row.gradedId });

  return (
    <svg viewBox={`0 0 ${LW} ${LH}`} className="block h-auto w-full" aria-hidden="true" data-label-finish={finishName}>
      <Face id={id} finish={finish} />

      <path d={backFramePath()} fill={ink} fillRule="evenodd" {...outline(inkStroke, 0.3)} />
      <CornerMark transform={BACK_LOGO_TRANSFORM} fill={ink} stroke={inkStroke} />

      {layout.name && <LabelText spec={layout.name} fill={ink} stroke={inkStroke} />}
      {layout.lines.map((spec, index) => (
        <LabelText key={`${index}-${spec.text}`} spec={spec} fill={ink} stroke={inkStroke} />
      ))}

      {/* The engraved code's own footprint: QR_SIZE at QR_X/QR_Y, in the label's ink. */}
      <path data-label-qr="" d={d} transform={`translate(${QR_X} ${QR_Y})`} fill={ink} />
      <LabelText spec={layout.cert} fill={ink} stroke={inkStroke} />
    </svg>
  );
}

function LabelPreview({ row, gameLabel, headingId }: { row: ReportRow; gameLabel: string; headingId: string }) {
  const { cardName, frontDetail, back } = selectLabelLines(row, gameLabel);

  return (
    <section aria-labelledby={headingId} className="skin-surface rounded-md border border-pcg-border bg-pcg-panel">
      <div className="flex items-center justify-between gap-3 border-b border-pcg-goldDark/20 px-4 py-2">
        <h2 id={headingId} className="m-0 text-[11px] font-semibold uppercase tracking-wider text-pcg-goldText">
          Label Preview
        </h2>
        <span className="text-[11px] text-pcg-textDim">Illustration only</span>
      </div>
      {/*
        The two faces of the label, described once in words: the drawing repeats the grade
        and card details already read out above, plus the QR code's target.
      */}
      <p className="sr-only">
        Drawing of the slab label: front and back, with a QR code linking to {reportUrlFor(row.gradedId)}.
      </p>
      <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2">
        <LabelFront row={row} cardName={cardName} detail={frontDetail} />
        <LabelBack row={row} cardName={cardName} lines={back} />
      </div>
    </section>
  );
}

export default LabelPreview;
